import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Home,
  Shield,
  Radio,
  Lock,
  Volume2,
  Lightbulb,
  Zap,
  ArrowRight,
  CheckCircle,
  Sparkles
} from "lucide-react";

export default function Homepage() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const services = [
    {
      icon: Home,
      key: "smartHomeAutomation",
      path: "/services/smart-home",
      color: "#4f46e5",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    },
    {
      icon: Shield,
      key: "securityAndSurveillanceSystem",
      path: "/services/security",
      color: "#dc2626",
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
    },
    {
      icon: Radio,
      key: "intercomSystem",
      path: "/services/intercom",
      color: "#059669",
      gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
    },
    {
      icon: Lock,
      key: "smartLockAndAccessControlSystems",
      path: "/services/smart-lock",
      color: "#d97706",
      gradient: "linear-gradient(135deg, #d97706 0%, #ffde21 100%)"
    },
    {
      icon: Volume2,
      key: "soundSystems",
      path: "/services/sound",
      color: "#7c3aed",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #c084fc 100%)"
    },
    {
      icon: Lightbulb,
      key: "lighteningSolutions",
      path: "/services/lightening",
      color: "#eab308",
      gradient: "linear-gradient(135deg, #ffd89b 0%, #19547b 100%)"
    },
    {
      icon: Zap,
      key: "electricalWiringAccessories",
      path: "/services/electrical",
      color: "#0891b2",
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)"
    }
  ];

  const features = [
    {
      icon: CheckCircle,
      title: t("homepage.features.guaranteed.title"),
      desc: t("homepage.features.guaranteed.desc")
    },
    {
      icon: Shield,
      title: t("homepage.features.safety.title"),
      desc: t("homepage.features.safety.desc")
    },
    {
      icon: Sparkles,
      title: t("homepage.features.modern.title"),
      desc: t("homepage.features.modern.desc")
    }
  ];

  // Supplier logos (repeat to ensure seamless track)
  const logos = ['/images/vimarlogo.webp', '/images/abblogo.webp', '/images/smarwish.webp'];

  return (
    <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 md:py-32">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Animated Background Shapes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -top-40 -left-40 w-80 h-80 rounded-full opacity-10"
            style={{ background: '#ffde21' }}
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -90, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full opacity-10"
            style={{ background: '#ffde21' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block mb-6"
            >
              <span
                className="px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  color: '#2d2d2b'
                }}
              >
                <Sparkles size={18} />
                {t("homepage.hero.badge")}
              </span>
            </motion.div>

            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight"
              style={{ color: '#2d2d2b' }}
            >
              {t("homepage.hero.title_line1")}
              <br />
              <span style={{ color: '#ffde21' }}>
                {t("homepage.hero.title_line2")}
              </span>
            </h1>

            <p
              className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto leading-relaxed"
              style={{ color: '#2d2d2b', opacity: 0.8 }}
            >
              {t("homepage.hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/services/smart-home">
                <button
                  className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 flex items-center gap-2"
                  style={{
                    background: '#ffde21',
                    color: '#2d2d2b'
                  }}
                >
                  {t("homepage.buttons.discoverServices")}
                  <ArrowRight size={20} />
                </button>
              </Link>

              <Link to="/design-home">
                <button
                  className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-xl"
                  style={{
                    background: 'transparent',
                    color: '#2d2d2b',
                    border: '2px solid #2d2d2b'
                  }}
                >
                  {dir === "rtl" ? "صمم منزلك" : "Design Your Home"}
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Suppliers Banner - Seamless Infinite Scroll (CSS-driven) */}
      <section className="py-8 overflow-hidden" style={{ background: '#2d2d2b' }}>
        <div className="relative overflow-hidden">
          <style>{`
            .supplier-track { display:flex; gap:2.5rem; align-items:center; }
            .supplier-item img { max-width:100%; max-height:100%; object-fit:contain; opacity:0.75; transition:opacity .25s ease; }
            .supplier-item img:hover { opacity:1; }
            @keyframes scroll-left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
            @keyframes scroll-right { 0% { transform: translateX(0); } 100% { transform: translateX(50%); } }
          `}</style>

          {/* Duplicate the logos several times so track is long enough for all viewports */}
          <div
            className="supplier-track-wrap"
            style={{ overflow: 'hidden' }}
          >
            <div
              className="supplier-track"
              style={{
                width: 'max-content',
                willChange: 'transform',
                animation: `${dir === 'rtl' ? 'scroll-right' : 'scroll-left'} 22s linear infinite`
              }}
            >
              {Array(4).fill(logos).flat().concat(Array(4).fill(logos).flat()).map((src, i) => (
                <div key={i} className="supplier-item flex items-center justify-center w-48 h-24">
                  <img src={src} alt={`supplier-${i}`} loading="lazy" decoding="async" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {t("homepage.whyChooseUs")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-8 rounded-2xl text-center transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.3)'
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>
                  <h3
                    className="text-xl font-bold mb-3"
                    style={{ color: '#2d2d2b' }}
                  >
                    {feature.title}
                  </h3>
                  <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                    {feature.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="py-16 md:py-24" style={{ background: 'rgba(255, 222, 33, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {t("homepage.services.title")}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {t("homepage.services.description")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    to={service.path}
                    className="block group relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl overflow-hidden"
                    style={{
                      background: 'white',
                      border: '2px solid rgba(255, 222, 33, 0.2)'
                    }}
                  >
                    {/* Gradient Background on Hover */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                      style={{ background: service.gradient }}
                    />

                    {/* Icon */}
                    <div
                      className="relative w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                      style={{ background: `${service.color}15` }}
                    >
                      <Icon size={32} style={{ color: service.color }} strokeWidth={2.5} />
                    </div>

                    {/* Title */}
                    <h3
                      className="relative text-xl font-bold mb-4"
                      style={{ color: '#2d2d2b' }}
                    >
                      {t(service.key)}
                    </h3>

                    {/* Arrow */}
                    <div
                      className="relative flex items-center gap-2 font-semibold transition-all duration-300 group-hover:gap-4"
                      style={{ color: service.color }}
                    >
                      <span className="text-sm">
                        {t("homepage.learnMore")}
                      </span>
                      <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </div>

                    {/* Bottom Accent */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"
                      style={{ background: service.color }}
                    />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* View All Services Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <button
                className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#2d2d2b',
                  color: 'white'
                }}
              >
                {t("homepage.viewAllServices")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: '#2d2d2b' }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-6"
              style={{ color: '#ffde21' }}
            >
              {t("homepage.cta.title")}
            </h2>
            <p
              className="text-lg md:text-xl mb-8"
              style={{ color: 'white', opacity: 0.9 }}
            >
              {t("homepage.cta.description")}
            </p>
            <Link to="/contact">
              <button
                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {t("homepage.cta.button")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}